/* Sprints (Modul aufgaben, ADR-011): Tabelle aufgaben_sprints statt der früheren View app_sprints im Kern.
   Lesen über RLS, Schreiben nur über RPCs. Neuladen alle 15 Sekunden reicht (ADR-007). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "../../kern/supabase";
import type { Sprint } from "./sprintlogik";

export function useSprints() {
  return useQuery({
    queryKey: ["sprints"],
    refetchInterval: 15_000,
    queryFn: async (): Promise<Sprint[]> => {
      const { data, error } = await db().from("aufgaben_sprints").select("id, name, beginn, ende, ziel, status, abgeschlossen_am").order("ende", { ascending: false });
      if (error) throw error;
      return data.filter((s): s is Sprint => s.status === "laufend" || s.status === "abgeschlossen");
    },
  });
}

export function laufenderSprint(s: readonly Sprint[] | undefined): Sprint | undefined {
  return s?.find((x) => x.status === "laufend");
}

export interface Ergebnis {
  sprint_id: string;
  aufgabe_id: string;
  ergebnis: "erledigt" | "naechster_sprint" | "backlog";
  titel: string;
  person: string | null;
}

/** Stand beim Abschluss je Aufgabe (Archiv). RLS: nur zu Aufgaben, die man sieht. */
export function useSprintErgebnisse(aktiv = true) {
  return useQuery({
    queryKey: ["sprint-ergebnisse"],
    enabled: aktiv,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Ergebnis[]> => {
      const { data, error } = await db().from("aufgaben_sprint_ergebnisse").select("sprint_id, aufgabe_id, ergebnis, titel, person");
      if (error) throw error;
      return data.filter((e): e is Ergebnis => e.ergebnis === "erledigt" || e.ergebnis === "naechster_sprint" || e.ergebnis === "backlog");
    },
  });
}

export interface Sitzungstermin {
  id: string;
  datum: string;
  uhrzeit: string | null;
  ort: string;
  status: "geplant" | "verschoben" | "abgesagt" | "abgeschlossen";
}

/** Vorstandssitzungen aus app_termine (Kern, nur gelesen): für „Nächste Sitzung“ und den Vorschlag fürs Ende. */
export function useSitzungstermine() {
  return useQuery({
    queryKey: ["termine", "sitzungen"],
    staleTime: 60_000,
    queryFn: async (): Promise<Sitzungstermin[]> => {
      const { data, error } = await db().from("app_termine").select("id, datum, uhrzeit, ort, status").eq("art", "vorstandssitzung").order("datum");
      if (error) throw error;
      return data as Sitzungstermin[];
    },
  });
}

/** Vorschlag der Datenbank für das Ende des nächsten Sprints (aufgaben_sprint_vorschlag, nur mit sprint.planen) */
export function useSprintVorschlag(aktiv: boolean) {
  return useQuery({
    queryKey: ["sprint-vorschlag"],
    enabled: aktiv,
    staleTime: 0,
    queryFn: async (): Promise<string> => {
      const { data, error } = await db().rpc("aufgaben_sprint_vorschlag");
      if (error) throw error;
      return data;
    },
  });
}

export function useSprintAktionen() {
  const qc = useQueryClient();
  const neu = () => {
    for (const k of ["sprints", "aufgaben", "aufgabe", "sprint-ergebnisse", "sprint-vorschlag", "mitteilungen", "verlauf"]) void qc.invalidateQueries({ queryKey: [k] });
  };
  return {
    bearbeiten: useMutation({
      mutationFn: async ({ sprint, ziel, ende }: { sprint: string; ziel?: string; ende?: string }) => {
        const { error } = await db().rpc("aufgaben_sprint_bearbeiten", { p_sprint: sprint, ...(ziel !== undefined ? { p_ziel: ziel } : {}), ...(ende !== undefined ? { p_ende: ende } : {}) });
        if (error) throw error;
      },
      onSettled: neu,
    }),
    abschliessen: useMutation({
      mutationFn: async ({ sprint, ende, ziel, inBacklog }: { sprint: string; ende: string; ziel: string; inBacklog: string[] }): Promise<string> => {
        const { data, error } = await db().rpc("aufgaben_sprint_abschliessen", { p_sprint: sprint, p_naechstes_ende: ende, p_naechstes_ziel: ziel, p_in_backlog: inBacklog });
        if (error) throw error;
        return data;
      },
      onSettled: neu,
    }),
  };
}
